import * as L from "leaflet";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet-search";
import "leaflet-edgebuffer";
import "leaflet-search/dist/leaflet-search.min.css";

import { createClusterGroup, wireGlobalMapEvents } from "./cluster";
import { createPorts, buildStationsFromMarkers, createPort } from "./data/searchIndex";
import { ApiPortEntry, captureFeatureView, SearchControl } from "../domain/visuals/SearchControl";
import { fetchStations, getPorts } from "../services/configApi";
import { getStationById, setStations } from "../domain/storage/stationsStore";
import { addAllStations, setPorts } from "../main";
import { fetchLatest } from "./data/fetchLatest";
import { createStatusLayerManager, getStatusManager, initStatusManager } from "./state/statusLayers";
import { getMarkers, markerToStation } from "./markers/createMarker";
import { COLOR_CODES, MAX_BY_TYPE } from "../domain/utils/colors";
import { getQueryValue } from "../domain/utils/urlUtils";
import { DataSampleType, Station } from "../domain/types";
import { attachMapForBatching, setupZoomBreakpointHandler } from "./markers/refresh";
import { getInvalidSamples, getLastUpdatedSampleTimestamp } from "./data/latestData";

let map: ReturnType<typeof createMap> | null = null;
export function initilizeMap(containerId: string, idealZoomLevel = 10) {
	if (!map) {
		map = createMap(containerId, idealZoomLevel);
	}
	return map;
}
export function getMap() {
	if (!map) {
		throw new Error("Map not initialized. Call initilizeMap first.");
	}
	return map;
}
function createMap(containerId: string, idealZoomLevel = 10) {
	const mapContainer = L.DomUtil.get(containerId) as any;
	if (mapContainer && mapContainer._leaflet_id) {
		const initMap = (L as any).Map.prototype._instances[mapContainer._leaflet_id];
		console.log("Map is initialized!!", initMap);

	}
	const mapConfiguration: L.MapOptions = {
		zoomSnap: 0.5,
		zoomDelta: 0.5,
		zoomControl: false,
		zoomAnimation: true,
		markerZoomAnimation: true,
		fadeAnimation: true
	}
	const tileConfiguration: L.TileLayerOptions = {
		attribution: "&copy; OpenStreetMap contributors",
		edgeBufferTiles: 2
	}
	if (L.Browser.mobile) {
		mapConfiguration.zoomAnimation = true;
		mapConfiguration.markerZoomAnimation = false;
		mapConfiguration.preferCanvas = true;
		mapConfiguration.fadeAnimation = true;

		//tileConfiguration.updateWhenIdle = false;
		tileConfiguration.edgeBufferTiles = 1;
		tileConfiguration.keepBuffer = 1;
		tileConfiguration.updateWhenZooming = true;
		//tileConfiguration.updateInterval = 32;
	}
	const map = L.map(containerId, mapConfiguration);
	const area: ApiPortEntry = {
		"Id": "",
		"Name": "PORT",
		"SouthWest": {
			"Latitude": 57.9815,
			"Longitude": -8.669
		},
		"NorthEast": {
			"Latitude": 71.0937,
			"Longitude": 31.0962
		}
	};
	const view = createPort(area);
	const bounds = L.latLngBounds(view.sw, view.ne);
	map?.fitBounds(bounds, { animate: false });

	L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", tileConfiguration).addTo(map);

	const cluster: L.MarkerClusterGroup = createClusterGroup();
	map.addLayer(cluster);

	const mapElement = map.getContainer() as HTMLElement;

	function clamp(n: number, min: number, max: number) {
		return Math.max(min, Math.min(max, n));
	}

	(window as any).zoomScale = 1;

	function updateZoomScale() {
		const z = map.getZoom();
		const raw = Math.pow(2, z - idealZoomLevel);
		(window as any).zoomScale = clamp(raw, 1, 1.2);
		mapElement.style.setProperty("--zoomScale", String((window as any).zoomScale));
	}
	map.whenReady(updateZoomScale);
	map.on("zoom", updateZoomScale);
	const stationIndices = buildStationsFromMarkers([]);
	const search = new SearchControl({
		position: "topleft",
		stations: stationIndices,
		ports: [],
		markerClusterGroup: cluster,
		placeholder: "Search...",
		maxResults: Number.MAX_VALUE,
		minChars: 1,
		onSelectStation: (pin) => {
			console.log("Selected station: ", pin.id, pin.name);
		},
		onSelectPort: (area) => {
			console.log("Selected port: ", area.id, area.name);
		},
	});
	const statusMgr = initStatusManager(map, true);
	addFilterableStatusLegend(map, statusMgr, "bottomleft");
	map.addControl(search);
	L.control.zoom({ position: "topleft" }).addTo(map);

	generateBoundsViewForPort(map, cluster);
	wireGlobalMapEvents(map, cluster);
	attachMapForBatching(map);
	setupZoomBreakpointHandler(map);
	return { map, cluster, search, updateZoomScale };
}
function getColorLegend(colors: Array<{ p: number; c: string }>, title: string = "") {
	const div = L.DomUtil.create("div", "color-legend-bar status-legend");

	const header = L.DomUtil.create("div", "clb-title", div);
	header.textContent = title || "Color guide";

	const content = L.DomUtil.create("div", "clb-content", div);
	content.innerHTML = `
		<div style="display: flex; justify-content: space-between;">
			<span>0m/s</span>
			<span style="font-weight: bold">Wind</span>
			<span>${MAX_BY_TYPE[DataSampleType.WIND_SPEED]}m/s</span>
		</div>
		<div style="display: flex; justify-content: space-between;">
			<span>0m/s</span>
			<span style="font-weight: bold">Current</span>
			<span>${MAX_BY_TYPE[DataSampleType.CURRENT_SPEED]}m/s</span>
		</div>
		<div style="display: flex; justify-content: space-between;">
			<span>0°C</span>
			<span style="font-weight: bold">Airtemp</span>
			<span>${MAX_BY_TYPE[DataSampleType.AIRTEMP]}°C</span>
		</div>
	`;

	const row = L.DomUtil.create("div", "clb-row", content);
	colors.forEach((c) => {
		const seg = L.DomUtil.create("div", "clb-segment", row);
		seg.style.background = c.c;
	});

	let isCollapsed = true;
	content.style.display = "none";
	header.style.cursor = "pointer";
	header.addEventListener("click", () => {
		isCollapsed = !isCollapsed;
		if (isCollapsed) {
			content.style.display = "none";
			header.classList.add("collapsed");
		} else {
			content.style.display = "";
			header.classList.remove("collapsed");
		}
	});

	L.DomEvent.disableClickPropagation(div);
	L.DomEvent.disableScrollPropagation(div);

	return div;
}

export function addFilterableStatusLegend(
	map: L.Map,
	mgr: ReturnType<typeof createStatusLayerManager>,
	position: L.ControlPosition = 'bottomleft'
) {
	const keyEnabled = 'breeze.statusFilters';
	const keyCollapsed = 'breeze.statusFilters.collapsed';
	const colorLegend = getColorLegend(COLOR_CODES);
	const Legend = L.Control.extend({
		options: { position },

		onAdd: () => {
			const logos = L.DomUtil.create("div", "map-logos");

			logos.appendChild(L.DomUtil.create("div", "map-logo-watermark breeze-logo"));
			logos.appendChild(L.DomUtil.create("div", "map-logo-watermark sm-logo"));
			const outsideOutsideDiv = L.DomUtil.create("div", "leaflet-control");
			const div = L.DomUtil.create('div', 'status-legend filterable collapsible leaflet-control');
			div.innerHTML = `
				<button class="legend-header" type="button" aria-expanded="true" aria-controls="legend-body">
				<span class="legend-title">Last update status</span>
				<span class="legend-summary" aria-hidden="true"></span>
				</button>
				<div id="legend-body" class="legend-body">
				<button data-k="fresh"   class="chip on">${iconFresh()} <span>Fresh (Up to 15min old)</span><em class="count">0</em></button>
				<button data-k="stale"   class="chip on">${iconStale()} <span>Stale (More than 15min old)</span><em class="count">0</em></button>
				<button data-k="offline" class="chip on">${iconOffline()} <span>Offline (More than 60min old)</span><em class="count">0</em></button>
				</div>
			`;
			//div.appendChild(colorLegend);
			const ref = L.DomUtil.create("a");
			ref.href = "https://www.scanmatic.com";
			ref.text = "Breeze is powered by Scanmatic";
			ref.target = "_blank";
			div.appendChild(logos);
			div.appendChild(ref);
			outsideOutsideDiv.appendChild(colorLegend);
			outsideOutsideDiv.appendChild(div);

			L.DomEvent.disableClickPropagation(outsideOutsideDiv);
			L.DomEvent.disableScrollPropagation(outsideOutsideDiv);

			const header = div.querySelector<HTMLButtonElement>('.legend-header')!;
			const body = div.querySelector<HTMLDivElement>('#legend-body')!;
			const summary = div.querySelector<HTMLSpanElement>('.legend-summary')!;

			// helpers
			const setCollapsed = (collapsed: boolean) => {
				div.toggleAttribute('data-collapsed', collapsed);
				header.setAttribute('aria-expanded', String(!collapsed));
				body.style.maxHeight = collapsed ? '0px' : body.scrollHeight + 'px';
				localStorage.setItem(keyCollapsed, JSON.stringify(collapsed));
			};

			const apply = () => {
				const { enabled, counts } = mgr.getState();
				(['fresh', 'stale', 'offline'] as const).forEach(k => {
					const btn = div.querySelector<HTMLButtonElement>(`[data-k="${k}"]`)!;
					btn.classList.toggle('on', enabled[k]);
					btn.querySelector<HTMLElement>('.count')!.textContent = String(counts[k]);
				});
				// Compact summary for collapsed state
				summary.textContent = `F ${counts.fresh} • S ${counts.stale} • O ${counts.offline}`;
				localStorage.setItem(keyEnabled, JSON.stringify(enabled));
				// keep height accurate when expanded and counts change
				if (!div.hasAttribute('data-collapsed')) {
					body.style.maxHeight = body.scrollHeight + 'px';
				}
			};

			// restore saved filters
			try {
				const saved = JSON.parse(localStorage.getItem(keyEnabled) || '{}');
				(['fresh', 'stale', 'offline'] as const).forEach(k => {
					if (typeof saved[k] === 'boolean') mgr.setEnabled(k, saved[k]);
				});
			} catch { }

			// restore collapsed state
			let collapsed = true;
			try { collapsed = JSON.parse(localStorage.getItem(keyCollapsed) || String(collapsed)); } catch { }
			// initial height before transition
			requestAnimationFrame(() => {
				body.style.maxHeight = collapsed ? '0px' : body.scrollHeight + 'px';
				setCollapsed(collapsed);
			});

			// toggle collapse
			header.addEventListener('click', () => setCollapsed(!div.hasAttribute('data-collapsed')));
			header.addEventListener('keydown', (e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault(); header.click();
				}
			});

			// chip clicks (toggle / Alt-click isolate)
			body.addEventListener('click', (e) => {
				const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.chip');
				if (!btn) return;
				const k = btn.getAttribute('data-k') as 'fresh' | 'stale' | 'offline';
				if ((e as MouseEvent).altKey) {
					(['fresh', 'stale', 'offline'] as const).forEach(s => mgr.setEnabled(s, s === k));
				} else {
					mgr.setEnabled(k, !btn.classList.contains('on'));
				}
				apply();
			});

			// live updates from manager
			mgr.subscribe(apply);
			apply();
			return outsideOutsideDiv;
		},

	});
	const ctl = new Legend();
	ctl.addTo(map);
	return ctl;
}

function iconFresh(size = 24) { return `<svg class="lgd-ico" width="${size}" height="${size}" viewBox="0 0 40 40"><circle cx="20" cy="20" r="18" fill="#90CAF9" fill-opacity=".18"/></svg>`; }
function iconStale(size = 24) { return `<svg class="lgd-ico" width="${size}" height="${size}" viewBox="0 0 40 40"><circle cx="20" cy="20" r="17" fill="none" stroke="#B0BEC5" stroke-width="3" stroke-dasharray="6 4"/></svg>`; }
function iconOffline(size = 24) { return `<svg class="lgd-ico" width="${size}" height="${size}" viewBox="0 0 40 40"><circle cx="20" cy="20" r="17" fill="none" stroke="#9E9E9E" stroke-width="3"/><line x1="12" y1="12" x2="28" y2="28" stroke="#BDBDBD" stroke-width="3" stroke-linecap="round"/><line x1="28" y1="12" x2="12" y2="28" stroke="#BDBDBD" stroke-width="3" stroke-linecap="round"/></svg>`; }

function cleanJSON(obj: any) {
	var cleaned = JSON.stringify(obj, null, 2);

	return cleaned.replace(/^[\t ]*"[^:\n\r]+(?<!\\)":/gm, function (match) {
		return match.replace(/"/g, "");
	});
}
export function generateBoundsViewForPort(map: L.Map, cluster: L.MarkerClusterGroup | null = null) {
	const captureView = () => {
		const groups = getStatusManager().clusters;
		let allMarkers: L.Marker[] = [];
		for (const groupCluster of groups) {
			const markers = getMarkers(map, groupCluster as L.MarkerClusterGroup, false);
			for (const m of markers) {
				allMarkers.push(m);
			}

		}
		const spec = captureFeatureView(allMarkers.map(o => ({ lat: o.getLatLng().lat, lng: o.getLatLng().lng })));
		return {
			Name: "PORT", SouthWest: { Latitude: spec.sw?.lat, Longitude: spec.sw?.lng }, NorthEast: { Latitude: spec.ne?.lat, Longitude: spec.ne?.lng }
		};
	};

	const onKey = async (e: KeyboardEvent) => {
		// Alt+Shift+B → log
		if (e.key.toLowerCase() === 'b' && e.shiftKey && e.altKey) {
			e.preventDefault();
			const view = captureView();
			console.log("Bounds: ", view);
		}
		// Ctrl+Shift+B → log + copy JSON to clipboard
		if (e.key.toLowerCase() === 'b' && e.shiftKey && (e.ctrlKey || e.metaKey)) {
			e.preventDefault();
			const data = captureView();
			const text = cleanJSON(data);
			try {
				await navigator.clipboard.writeText(text);
				console.log("📋 Copied bounds to clipboard:", text);
			} catch {
				console.warn("Clipboard write failed (likely due to permissions).");
			}
		}

		if (e.key.toLowerCase() === "s" && e.shiftKey && e.altKey) {
			e.preventDefault();
			const groups = getStatusManager().clusters;
			const list: {
				station: Station, state: string, popup: string, marker: L.Marker, percentageOKSamples: number, invalidStat: any, lastTimestamp: Date
			}[] = [];
			for (const groupCluster of groups) {
				const markers = getMarkers(map, groupCluster as L.MarkerClusterGroup, false);
				for (const m of markers) {
					const stationId = markerToStation.get(m);
					const station = getStationById(stationId);
					if (station) {
						const invalidStat = getInvalidSamples(station);
						const lastTimestamp = getLastUpdatedSampleTimestamp(station);
						list.push({ marker: m, station: station, state: (m as any).__state.status ?? "", popup: m.getPopup()?.getContent()?.toString() ?? "", percentageOKSamples: (invalidStat.valid / invalidStat.total) * 100, invalidStat: invalidStat, lastTimestamp });
					}
				}

			}

			const text = list.map((value) => {
				return value.station.Id + "\t" + value.station.Name + "\t" + value.station.Owner + "\t" + value.state + "\t" + value.percentageOKSamples + "\t" + value.lastTimestamp;
			});
			console.log("StationId\tName\tOwner\tStatus\tPercentageOKSamples\tLast timestamp\n" + text.join("\n"));
		}
	};

	// Use document so it works even if the map isn’t focused
	document.addEventListener('keydown', onKey);

	// Optional: clean-up helper
	return () => document.removeEventListener('keydown', onKey);
}

export function showLoading(message: string) {
	console.warn("Loading message: ", message);
	const overlay = document.getElementById("map-loading");
	const text = document.getElementById("loading-text");
	if (overlay && text) {
		text.textContent = message;
		overlay.classList.remove("hidden");
	}
}

export function hideLoading() {
	const overlay = document.getElementById("map-loading");
	if (overlay) {
		overlay.classList.add("hidden");
	}

}

export async function loadConfiguration(isLoading: boolean = true) {
	if (isLoading) {
		showLoading("Updating map...");
	}
	const stations = await fetchStations();
	if (stations) {
		setStations(stations);
	} else {
		console.error("Error loading stations");
	}
	addAllStations();
	if (isLoading) {
		showLoading("Loading data...");
	}
	const data = await fetchLatest();
	if (!data) {
		console.error("Error loading data");
	}
	const ports = await getPorts();
	if (ports) {
		setPorts(createPorts(ports));
		console.log("Ports:", ports);
	}
	hideLoading();

	//const stationParam = getQueryValue("station");
	//if (stationParam && stations?.some(o => o.Id.toLowerCase() === stationParam.toLowerCase())) {
	//	getMap().search.selectStation(stationParam, true);
	//}
	const portParam = getQueryValue("port")
	if (portParam && ports?.some(o => o.Id.toLowerCase() === portParam.toLowerCase())) {
		getMap().search.selectPort(portParam);
	}
}
